import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Plus, BarChart2, X } from "lucide-react";
import { apiUrl } from "../../config/Config";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from "recharts";

const Campaigntest = () => {
  const userdata = useSelector((store) => store.userReducer.userdata);

  const [platform, setPlatform] = useState("All");
  const [status, setStatus] = useState("All");
  const [facebookAccounts, setFacebookAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");

  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reportModal, setReportModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // ---------------- FETCH FACEBOOK ACCOUNTS ----------------
  const fetchAdAccounts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(apiUrl + "facebook_ad_accounts", {
        headers: { Authorization: "Bearer " + userdata?.token },
      });
      const accounts = res.data?.data?.data || [];
      setFacebookAccounts(accounts);

      if (accounts.length > 0) {
        setSelectedAccount(accounts[0].id);
        fetchCampaigns(accounts[0].id);
      }
    } catch (err) {
      console.error("❌ Fetch Accounts Error", err);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- FETCH CAMPAIGNS ----------------
  const fetchCampaigns = async (adAccountId) => {
    if (!adAccountId) return;
    try {
      setLoading(true);
      const res = await axios.get(apiUrl + "facebook_campaigns_list", {
        params: { ad_account_id: adAccountId },
        headers: { Authorization: "Bearer " + userdata?.token },
      });

      const list = (res.data?.data?.data || []).map((item) => ({
        ...item,
        platforms: [{ platform: "facebook", spend: item.spend || 0, clicks: item.clicks || 0, conversions: item.conversions || 0 }],
        spend: item.spend || 0,
        budget: item.budget || 0,
        clicks: item.clicks || 0,
        conversions: item.conversions || 0,
      }));

      setCampaigns(list);
    } catch (err) {
      console.error("❌ Fetch Campaigns Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- FETCH CAMPAIGN INSIGHTS ----------------
  const fetchCampaignInsights = async (campaignId) => {
    try {
      setLoading(true);
      const res = await axios.get(apiUrl + "facebook_campaigns_insights", {
        params: { campaign_id: campaignId },
        headers: { Authorization: "Bearer " + userdata?.token },
      });
      return res.data?.data?.data || null;
    } catch (err) {
      console.error("❌ Fetch Insights Error", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdAccounts();
  }, []);

  const handleViewDetails = async (campaign) => {
    const insights = await fetchCampaignInsights(campaign.id);
    const normalizedPlatforms =
      insights?.platforms?.map((p) => ({
        platform: p.platform,
        spend: p.spend,
        clicks: p.clicks,
        conversions: p.conversions,
      })) || campaign.platforms;

    setSelectedCampaign({ ...campaign, platforms: normalizedPlatforms, budget: insights?.budget || campaign.budget });
    setIsModalOpen(true);
  };

  const filteredCampaigns = campaigns.filter((c) => {
    const platformMatch =
      platform === "All" || c.platforms.some((p) => p.platform?.toLowerCase() === platform.toLowerCase());
    const statusMatch = status === "All" || c.status === status;
    return platformMatch && statusMatch;
  });

  // ---------------- CAMPAIGN DETAIL MODAL ----------------
  const CampaignDetailModal = ({ campaign, onClose }) => {
    const platforms = campaign?.platforms || [];
    const [selectedPlatform, setSelectedPlatform] = useState(platforms[0]?.platform || "");

    if (!campaign) return null;

    const trendChartData = selectedPlatform
      ? [
          {
            name: "Week 1",
            Conversions: Math.round((platforms.find((p) => p.platform === selectedPlatform)?.conversions || 0) * 0.25),
            Clicks: Math.round((platforms.find((p) => p.platform === selectedPlatform)?.clicks || 0) * 0.1),
          },
          {
            name: "Week 2",
            Conversions: Math.round((platforms.find((p) => p.platform === selectedPlatform)?.conversions || 0) * 0.5),
            Clicks: Math.round((platforms.find((p) => p.platform === selectedPlatform)?.clicks || 0) * 0.4),
          },
          {
            name: "Week 3",
            Conversions: Math.round((platforms.find((p) => p.platform === selectedPlatform)?.conversions || 0) * 0.8),
            Clicks: Math.round((platforms.find((p) => p.platform === selectedPlatform)?.clicks || 0) * 0.7),
          },
          {
            name: "Week 4",
            Conversions: platforms.find((p) => p.platform === selectedPlatform)?.conversions || 0,
            Clicks: platforms.find((p) => p.platform === selectedPlatform)?.clicks || 0,
          },
        ]
      : [];

    const budgetChartData = selectedPlatform
      ? [
          {
            name: "Budget Overview",
            Budget: campaign.budget,
            Spend: platforms.find((p) => p.platform === selectedPlatform)?.spend || 0,
            Remaining: campaign.budget - (platforms.find((p) => p.platform === selectedPlatform)?.spend || 0),
          },
        ]
      : [];

    return (
      <div className="create-new-campaign-modal-popup-overlay" onClick={onClose}>
        <div className="create-new-campaign-modal-popup-content" onClick={(e) => e.stopPropagation()}>
          <div className="create-new-campaign-modal-popup-header">
            <h2>{campaign.campaign_name || campaign.name} - Detailed Performance</h2>
            <X size={24} onClick={onClose} />
          </div>

          <div className="create-new-campaign-deatils-modal-dropdown-box">
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="create-new-campaign-dropdown"
              style={{ width: "180px" }}
            >
              {platforms.map((p) => p.platform && <option key={p.platform} value={p.platform}>{p.platform.charAt(0).toUpperCase() + p.platform.slice(1)}</option>)}
            </select>
          </div>

          <div className="create-new-campaign-modal-popup-metrics">
            <p><strong>Platform:</strong> {selectedPlatform.toUpperCase()}</p>
            <p><strong>Total Spend:</strong> ${platforms.find((p) => p.platform === selectedPlatform)?.spend || 0}</p>
            <p><strong>Total Conversions:</strong> {platforms.find((p) => p.platform === selectedPlatform)?.conversions || 0}</p>
            <p><strong>Clicks (Total):</strong> {platforms.find((p) => p.platform === selectedPlatform)?.clicks || 0}</p>
          </div>

          <div className="create-new-campaign-modal-popup-chart-container">
            <div className="create-new-campaign-modal-popup-chart-box">
              <h3>Budget Allocation - {selectedPlatform.toUpperCase()}</h3>
              <div className="chart-wrapper">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={budgetChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" width={100} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Budget" fill="#ad7474ff" name="Total Budget" />
                    <Bar dataKey="Spend" fill="#8884d8" name="Current Spend" />
                    <Bar dataKey="Remaining" fill="#84d896ff" name="Remaining Budget" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="create-new-campaign-modal-popup-chart-box">
              <h3>Performance Trend - {selectedPlatform.toUpperCase()}</h3>
              <div className="chart-wrapper">
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={trendChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis yAxisId="conversions" stroke="#82ca9d" />
                    <YAxis yAxisId="clicks" orientation="right" stroke="#ff7300" />
                    <Tooltip />
                    <Legend />
                    <Line yAxisId="conversions" dataKey="Conversions" stroke="#82ca9d" strokeWidth={2} />
                    <Line yAxisId="clicks" dataKey="Clicks" stroke="#ff7300" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

// ---------------- GENERATE REPORT MODAL (EXACT YOUR FLOW) ----------------
const GenerateReportModal = ({ campaigns, onClose }) => {
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [reportName, setReportName] = useState("");

  const filtered = campaigns;

  const toggleSelect = (id) => {
    setSelectedCampaigns((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  };

  const handleSubmit = () => setSubmitted(true);

  const selectedData = campaigns.filter((c) => selectedCampaigns.includes(c.id));

  // convert chart to PNG
  const serializeChartToPngDataUrl = async () => {
    try {
      const container = document.querySelector(
        ".create-new-campaign-modal-popup-genrate-report-chart"
      );
      if (!container) return null;

      const svg = container.querySelector("svg");
      if (!svg) return null;

      const serializer = new XMLSerializer();
      let svgStr = serializer.serializeToString(svg);

      if (
        !svgStr.match(
          /^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/
        )
      ) {
        svgStr = svgStr.replace(
          /^<svg/,
          '<svg xmlns="http://www.w3.org/2000/svg"'
        );
      }

      const blob = new Blob([svgStr], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);

      const img = new Image();
      img.crossOrigin = "Anonymous";

      return await new Promise((resolve) => {
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width || 800;
          canvas.height = img.height || 400;
          const ctx = canvas.getContext("2d");

          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          ctx.drawImage(img, 0, 0);
          URL.revokeObjectURL(url);

          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(null);
        };
        img.src = url;
      });
    } catch (err) {
      console.error("chart capture failed", err);
      return null;
    }
  };

  // SAVE REPORT
  const handleSaveReport = async () => {
    if (reportName.trim() === "") {
      alert("Please enter a report name before saving!");
      return;
    }

    const chartDataUrl = await serializeChartToPngDataUrl();
    const existing = JSON.parse(localStorage.getItem("savedReports")) || [];
    const currentUser =
      JSON.parse(localStorage.getItem("currentUser")) || {
        name: "Kiran Nandi",
      };

    const newReport = {
      id: Date.now(),
      name: reportName,
      generatedBy: currentUser.name || "Unknown",
      createdAt: new Date().toLocaleString(),
      chartData: chartDataUrl,
      campaigns: selectedData,
    };

    existing.push(newReport);
    localStorage.setItem("savedReports", JSON.stringify(existing));

    alert(`Report "${reportName}" saved successfully!`);
    setReportName("");
  };

  return (
    <div
      className="create-new-campaign-modal-popup-overlay"
      onClick={onClose}
    >
      <div
        className="create-new-campaign-modal-popup-genrate-report-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="create-new-campaign-modal-popup-genrate-report-header">
          <h2>📊 Generate Campaign Report</h2>
          <X
            size={24}
            className="create-new-campaign-modal-popup-genrate-report-close"
            onClick={onClose}
          />
        </div>

        <div className="create-new-campaign-modal-popup-genrate-report-body">
          {/* LEFT */}
          <div className="create-new-campaign-modal-popup-genrate-report-left">
            <h3>Select Campaigns</h3>

            <div className="create-new-campaign-modal-popup-genrate-report-campaign-list">
              {filtered.map((c) => (
                <label
                  key={c.id}
                  className="create-new-campaign-modal-popup-genrate-report-item"
                >
                  <input
                    type="checkbox"
                    checked={selectedCampaigns.includes(c.id)}
                    onChange={() => toggleSelect(c.id)}
                  />

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {c.name}

                    {/* PLATFORM ICONS EXACT LIKE YOUR CODE */}
                    {c.platforms?.some((p) => p.platform === "facebook") && (
                      <i
                        className="fa-brands fa-facebook-f"
                        style={{ fontSize: "14px", color: "#3b5998" }}
                      ></i>
                    )}

                    {c.platforms?.some((p) => p.platform === "instagram") && (
                      <i
                        className="fa-brands fa-instagram"
                        style={{ fontSize: "14px", color: "#c32aa3" }}
                      ></i>
                    )}

                    {c.platforms?.some((p) => p.platform === "linkedin") && (
                      <i
                        className="fa-brands fa-linkedin-in"
                        style={{ fontSize: "14px", color: "#0077b5" }}
                      ></i>
                    )}
                  </div>
                </label>
              ))}
            </div>

            <button
              className="create-new-campaign-modal-popup-genrate-report-submit-btn"
              onClick={handleSubmit}
            >
              Submit
            </button>
          </div>

          {/* RIGHT */}
          <div className="create-new-campaign-modal-popup-genrate-report-right">
            {submitted && selectedData.length > 0 ? (
              <>
                <h3>Comparison Summary</h3>

                <table className="create-new-campaign-modal-popup-genrate-report-table">
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Spend ($)</th>
                      <th>Budget ($)</th>
                      <th>Clicks</th>
                      <th>Conversions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedData.map((c) => (
                      <tr key={c.id}>
                        <td>{c.name}</td>
                        <td>{c.spend}</td>
                        <td>{c.budget}</td>
                        <td>{c.clicks}</td>
                        <td>{c.conversions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="create-new-campaign-modal-popup-genrate-report-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={selectedData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="spend" fill="#8884d8" name="Spend" />
                      <Bar
                        dataKey="conversions"
                        fill="#82ca9d"
                        name="Conversions"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="create-new-campaign-modal-popup-genrate-report-save-section">
                  <input
                    type="text"
                    className="create-new-campaign-modal-popup-genrate-report-input"
                    placeholder="Enter Report Name"
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                  />

                  <button
                    className="create-new-campaign-modal-popup-genrate-report-save-btn"
                    onClick={handleSaveReport}
                  >
                    Save Report
                  </button>
                </div>
              </>
            ) : (
              <p className="create-new-campaign-modal-popup-genrate-report-no-data">
                Select campaigns and click “Submit” to view comparison.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};



  // ---------------- MAIN RENDER ----------------
  return (
    <div className="create-new-campaign-container">
      {isModalOpen && selectedCampaign && (
        <CampaignDetailModal campaign={selectedCampaign} onClose={() => setIsModalOpen(false)} />
      )}

   {reportModal && (
  <GenerateReportModal
    campaigns={campaigns}
    onClose={() => setReportModal(false)}
  />
)}


      <div className="create-new-campaign-header">
        <p className="create-new-campaign-subtitle">Here’s an overview of your campaigns.</p>
        <div style={{ display: "flex", gap: "10px" }}>
          <select
            className="create-new-campaign-dropdown"
            value={selectedAccount}
            onChange={(e) => {
              setSelectedAccount(e.target.value);
              fetchCampaigns(e.target.value);
            }}
          >
            <option value="">Select Account</option>
            {facebookAccounts.map((acc) => (
              <option key={acc.id} value={acc.id}>{acc.name}</option>
            ))}
          </select>

          <button className="create-new-campaign-btn" onClick={() => setReportModal(true)}>
            <Plus size={18} /> Generate Report
          </button>
        </div>
      </div>

      <div className="create-new-campaign-filters">
        <select className="create-new-campaign-dropdown" value={platform} onChange={(e) => setPlatform(e.target.value)}>
          <option value="All">Platform: All</option>
          <option value="facebook">Facebook</option>
          <option value="instagram">Instagram</option>
          <option value="linkedin">LinkedIn</option>
        </select>
        <select className="create-new-campaign-dropdown" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="All">Status: All</option>
          <option value="Active">Active</option>
          <option value="Paused">Paused</option>
        </select>
      </div>

     <div className="create-new-campaign-grid">
  {campaigns.map((c) => (
    <div key={c.id} className="create-new-campaign-card">
      <div className="create-new-campaign-card-header">
        <h4 className="campaign-name-title">{c.campaign_name || c.name}</h4>
        <BarChart2 size={18} className="campaign-details-icon" onClick={() => handleViewDetails(c)} />
      </div>

      <div className="create-new-campaign-status-row">
        <span className="create-new-campaign-status active"></span>
        <span className="create-new-campaign-status-text">{c.status}</span>
      </div>

      {/* Show platform icons like Facebookcampaign */}
      {/* <div className="create-new-campaign-platforms">
        {c.platforms?.map((p) => {
          if (!p.platform) return null;
          return (
            <div key={p.platform} className={`create-new-campaign-icon-circle ${p.platform.substring(0,2)}`}>
              {p.platform === "facebook" && <i className="fa-brands fa-facebook-f"></i>}
              {p.platform === "instagram" && <i className="fa-brands fa-instagram"></i>}
              {p.platform === "linkedin" && <i className="fa-brands fa-linkedin-in"></i>}
            </div>
          );
        })}
      </div> */}

      <div className="create-new-campaign-info">
        <p>Spend: <strong>${c.spend.toLocaleString()}</strong> / ${c.budget.toLocaleString()}</p>
        <p>Conversions: {c.conversions}</p>
      </div>

      <div className="create-new-campaign-progress">
        <div
          className="create-new-campaign-progress-bar"
          style={{ width: `${(c.spend / c.budget) * 100}%` }}
        ></div>
      </div>

      <div className="create-new-campaign-footer">
        <span>Clicks: {c.clicks.toLocaleString()}</span>
        <span>{c.conversions} conversions</span>
      </div>
    </div>
  ))}
</div>

    </div>
  );
};

export default Campaigntest;
